/**
 * This module encapsulates moment and the consumer doesn't have to know
 * anything about moment. The functions in this module just return native JS
 * data types like Regular Expressions, Strings and Booleans
 *
 * Each field must be 2 characters. For example, month must be '02' not '2'
 * Year must be > 2000, so validated date can be parsed into a JS Date object
 *
 * Source for rules: http://en.wikipedia.org/wiki/Date_format_by_country
 */

/**
 * @param momentDateFormat: string for moment formatting
 */
module.exports = function getDateFormatterObject (momentDateFormat) {
	'use strict';

	var moment = require('moment');

	/**
	 * Replace the parseTwoDigitYear method, otherwise moment says two digit years
	 * above 68 are assumed to be in the 1900's and years below 68 are assumed to
	 * be in the 2000's */
	moment.parseTwoDigitYear = function (twoDigits) {
		return 2000 + parseInt(twoDigits, 10);
	};

	/**
	 * Given a string that's validated by this module, and a format that's been
	 * returned by this module, return string date in 'YYYY-MM-DD' order to
	 * prepare it for passing to back-end.
	 *
	 * Note that `moment` doesn't care that much about leading zeroes or separators:
	 * > moment('1/2/03', 'MM-DD-YY').format('YYYY-MM-DD')
	 * '2003-01-02'
	 *
	 * This only allow years > 2000. This function will add years to the date
	 * if it detects the year in dateStr is < 2000
	 *
	 * @return string date in 'YYYY-MM-DD' format
	 */
	function normalizeDateString (dateStr) {
		var momentDate = moment(dateStr, momentDateFormat);
		return momentDate.format('YYYY-MM-DD');
	}

	/**
	 * Given a 'YYYY-MM-DD' date string, return a string date in the momentDateFormat format.
	 * Should only be used for initializing retained custom search
	 * @param dateStr - a date string in 'YYYY-MM-DD' format
	 * @param dateStr - could also be a momentJS object
	 * @return string date in whatever format is momentDateFormat
	 */
	function denormalizeDateString (dateStr) {
		return moment(dateStr, 'YYYY-MM-DD').format(momentDateFormat);
	}

	/**
	 * Format date string typed by customer to the format we suggest, which is
	 * determined by Griffin.
	 *
	 * @return date string formatted per dateFormatString, or false if invalid date
	 */
	function formatDateString (dateStr) {
		// have to pretend input always a 4-digit year to parse it as moment object
		var momentDateFormatFourYear = momentDateFormat.replace('YY', 'YYYY');

		// dateStr is taken in according to momentDateFormat and gets
		// re-formatted as momentDateFormat
		var formattedString = moment(dateStr, momentDateFormatFourYear).format(momentDateFormat);

		if (formattedString === 'Invalid date') {
			return false; // return value must be checked
		}

		return formattedString;
	}

	/**
	 * Wrapper for moment.isValid() that handles the 3 different date formats
	 * and adds biz logic that the string has to be at least 6 characters
	 * @return boolean - true if date is valid
	 */
	function isValidDateString (dateStr) {
		return dateStr.length > 5 && moment(dateStr, momentDateFormat).isValid();
	}

	/**
	 * Some private helper functions to figure why dateString is invalid and
	 * return a string to key the reason, or return undefined if can't figure
	 * out why or not invalid
	 *
	 * http://momentjs.com/docs/#/parsing/is-valid/ */
	// private methods
	function privateInvalidBecauseMonth (dateString, flags) {
		return flags.overflow === 1;
	}
	function privateInvalidBecauseDay (dateString, flags) {
		return flags.overflow === 2;
	}
	// public method
	function getReasonInvalid (dateString) {
		var mDate = moment(dateString, momentDateFormat),
			flags = mDate.parsingFlags();
		var reason;

		if (privateInvalidBecauseMonth(dateString, flags)) {
			reason = 'month'; // invalid month
		} else if (privateInvalidBecauseDay(dateString, flags)) {
			reason = 'day'; // invalid day
		} else {
			reason = 'format'; // invalid format
		}

		return reason;
	}

	return {
		momentDateFormat: momentDateFormat,
		normalizeDateString: normalizeDateString,
		denormalizeDateString: denormalizeDateString,
		formatDateString: formatDateString,
		isValidDateString: isValidDateString,
		getReasonInvalid: getReasonInvalid
	};
};



//////////////////
// WEBPACK FOOTER
// ./widgets/dateFormat.js
// module id = 776
// module chunks = 0